import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/constants';
import { getAuthToken } from '../utils/cookies';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export interface AuthResult {
  userId: string;
  isAnonymous: boolean;
  email?: string;
}

/**
 * Ensure user is authenticated.
 * Tries to use sb-auth-token cookie from auth.hollaco.com first,
 * falls back to anonymous auth if no cookie or invalid.
 */
export async function ensureAuth(): Promise<AuthResult> {
  // Check existing session
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user) {
    return {
      userId: session.user.id,
      isAnonymous: session.user.is_anonymous ?? false,
      email: session.user.email,
    };
  }

  // Try to authenticate from cookie
  const authToken = getAuthToken();
  if (authToken) {
    const { data, error } = await supabase.auth.setSession({
      access_token: authToken.access_token,
      refresh_token: authToken.refresh_token,
    });

    if (!error && data.session?.user) {
      console.log('[Auth] Authenticated from cookie:', data.session.user.id);
      return {
        userId: data.session.user.id,
        isAnonymous: false,
        email: data.session.user.email,
      };
    }
    console.warn('[Auth] Cookie auth failed, falling back to anonymous');
  }

  // Fall back to anonymous auth
  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) throw new Error(`Auth failed: ${error.message}`);

  console.log('[Auth] Signed in anonymously:', data.user!.id);
  return {
    userId: data.user!.id,
    isAnonymous: true,
  };
}
